import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Paperclip, Download, Trash2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { getBill } from '../../../../services/finance_services/billService';
import { getBillAttachments, deleteBillAttachment, downloadBillAttachment } from '../../../../services/finance_attachment_services/billAttachmentService';
import { exportBillPDF, exportBillExcel } from '../../../../services/export_services/billExportService';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const BillDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [attachments, setAttachments] = useState([]);
  const [deletingAttachments, setDeletingAttachments] = useState([]);

  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [notification, setNotification] = useState(null);

  // Use useApi for fetching bill
  const { 
    data: bill, 
    loading, 
    error, 
    callApi: fetchBill,
    setError 
  } = useApi(getBill, {showSuccessToast: false});




  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const result = await exportBillExcel(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Excel download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Excel download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadClick = async () => {
    setDownloading(true);
    try {
      const result = await exportBillPDF(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchBill(id);
        const attachResp = await getBillAttachments(id);
        setAttachments(attachResp?.data || []);
      } catch (err) {
        console.error('Failed to fetch attachments:', err);
      }
    };
    fetchData();
  }, [id]);

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setDeletingAttachments(prev => [...prev, attachmentId]);
      await deleteBillAttachment(id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAttachments(prev => prev.filter(id => id !== attachmentId));
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      setNotification({ type: 'loading', message: 'Downloading attachment...' });
      const result = await downloadBillAttachment(id, attachmentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-[300px]">Loading...</div>;
  }
  if (error) {
    return <Message type="error" message={error} />;
  }
  if (!bill) {
    return <div className="text-gray-500">Bill not found.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Notification toast */}
      {notification && (
        <div className={`fixed top-5 right-5 z-50 flex items-center p-4 rounded-lg shadow-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-primary-50 text-primary-800 border border-primary-200'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'loading' && 
            <div className="h-5 w-5 mr-2 border-2 border-t-primary-600 border-r-primary-600 border-b-primary-100 border-l-primary-100 rounded-full animate-spin"></div>
          }
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => navigate('/dashboard/bills')}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bills
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Bill Details</h1>
        </div>
        <div className="flex items-center space-x-3">
          {/* Download Buttons */}
          <button
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            onClick={handleDownloadClick}
            disabled={downloading}
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          
          <button
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
          >
            <Download className="h-4 w-4 mr-2" />
            {downloadingExcel ? 'Downloading...' : 'Download Excel'}
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Bill Details */}
        <div>
          <div className="font-semibold text-gray-700 mb-3">Bill Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><strong>Title:</strong> {bill.title}</div>
            <div><strong>Vendor:</strong> {bill.vendor}</div>
            <div><strong>Bill Number:</strong> {bill.bill_number}</div>
            <div><strong>Amount:</strong> {bill.amount}</div>
            <div><strong>Status:</strong> {bill.status_display || bill.status}</div>
            <div><strong>Date Issued:</strong> {bill.date_issued}</div>
            <div><strong>Due Date:</strong> {bill.due_date}</div>
            <div><strong>Amount Paid:</strong> {bill.amount_paid}</div>
            <div><strong>Payment Date:</strong> {bill.payment_date}</div>
            <div><strong>Payment Reference:</strong> {bill.payment_reference}</div>
            <div><strong>Recurring:</strong> {bill.is_recurring ? 'Yes' : 'No'}</div>
            {bill.is_recurring && (
              <>
                <div><strong>Recurrence Frequency:</strong> {bill.recurrence_frequency}</div>
                <div><strong>Recurrence End Date:</strong> {bill.recurrence_end_date}</div>
              </>
            )}
            <div className="md:col-span-2"><strong>Description:</strong> {bill.description}</div>
          </div>
        </div>
        
        {/* Attachments */}
        <div>
          <h3 className="font-semibold text-gray-700 mb-3">Attachments</h3>
          {attachments.length === 0 ? (
            <div className="text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No attachments found.</div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="divide-y divide-gray-200">
                {attachments.map(attachment => (
                  <li key={attachment.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{attachment.title || attachment.file_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(attachment.id)}
                        className="inline-flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        className="inline-flex items-center px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                        disabled={deletingAttachments.includes(attachment.id)}
                      >
                        {deletingAttachments.includes(attachment.id) ? (
                          'Deleting...'
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </>
                        )}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillDetailPage;